import { Component, OnInit } from '@angular/core';
import { ToastService } from '../../../services/toast.service';
import { EnrollmentService } from '../../../services/enrollment.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '../../../services/loading.service';
import { PortfolioMovementService, PortfolioMovementResponse, PortfolioMovementType } from '../../../services/portfolio-movement.service';
import { ExchangeRateService } from '../../../services/exchange-rate.service';

/**
 * @deprecated Este componente está deprecado. Usar ViewEnrollmentV2Component en su lugar.
 * Este componente se mantiene por compatibilidad pero será eliminado en futuras versiones.
 * La nueva versión incluye mejor visualización de comprobantes anulados y historial de cambios.
 */
@Component({
  selector: 'app-view-enrollment',
  templateUrl: './view-enrollment.component.html',
  styleUrl: './view-enrollment.component.scss'
})
export class ViewEnrollmentComponent implements OnInit {

  enrollment!: any;
  generalIncomeReceipts: any[] = []; // 🆕 Ingresos generales (sin plan de pago)
  movementHistory: PortfolioMovementResponse[] = []; // 🆕 Historial de movimientos de cartera
  today = new Date();

  // 🆕 Última cotización vigente para planes pendientes
  currentExchangeRate: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private enrollmentService: EnrollmentService,
    private portfolioMovementService: PortfolioMovementService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private exchangeRateService: ExchangeRateService
  ) {

  }

  ngOnInit(): void {
    this.loadEnrollment();
  }

  loadEnrollment(): void {
    const enrollmentId = this.route.snapshot.params['id'];

    if (!enrollmentId) {
      this.toastService.showToast('error', 'ID de inscripción no proporcionado.');
      return;
    }

    this.loadingService.show();
    this.enrollmentService.getById(enrollmentId).subscribe({
      next: (response) => {
        this.enrollment = response.data;
        // 🆕 Cargar ingresos generales después de cargar la inscripción
        this.loadGeneralIncomeReceipts(enrollmentId);
        // 🆕 Cargar historial de movimientos de cartera
        this.loadMovementHistory(enrollmentId);
        this.loadingService.hide();
      },
      error: (err) => {
        console.error('Error al cargar la inscripción:', err);
        this.toastService.showToast('error', 'No se pudo cargar la inscripción.');
        this.loadingService.hide();
      }
    });
  }

  // Helper para extraer datos de estructura paginada o array directo
  private extractData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  /**
   * 🆕 Carga los ingresos generales (sin plan de pago) de la inscripción
   * Estos son pagos por servicios adicionales, certificados, etc.
   */
  loadGeneralIncomeReceipts(enrollmentId: number): void {
    this.enrollmentService.getGeneralIncomeReceipts(enrollmentId).subscribe({
      next: (response) => {
        this.generalIncomeReceipts = this.extractData(response);
        console.log('📋 Ingresos generales cargados:', this.generalIncomeReceipts.length);
      },
      error: (err) => {
        console.error('Error al cargar ingresos generales:', err);
        this.generalIncomeReceipts = [];
        // No mostrar error al usuario, solo log, ya que es opcional
      }
    });
  }

  /**
   * 🆕 Carga el historial de movimientos de cartera de la inscripción
   */
  loadMovementHistory(enrollmentId: number): void {
    this.portfolioMovementService.getMovementHistory(enrollmentId).subscribe({
      next: (response) => {
        this.movementHistory = this.extractData(response);
        console.log('📋 Historial de movimientos cargado:', this.movementHistory.length);
      },
      error: (err) => {
        console.error('Error al cargar historial de movimientos:', err);
        this.movementHistory = [];
      }
    });
  }

  /**
   * 🆕 Verifica si hay historial de movimientos
   */
  hasMovementHistory(): boolean {
    return this.movementHistory && this.movementHistory.length > 0;
  }

  /**
   * 🆕 Obtiene el icono del tipo de movimiento
   */
  getMovementIcon(movementType: number | string): string {
    const type = Number(movementType);
    switch (type) {
      case PortfolioMovementType.Withdrawal: return '⚠️';
      case PortfolioMovementType.WithdrawalReversal: return '✅';
      case PortfolioMovementType.Deferral: return '⏸️';
      default: return '📋';
    }
  }

  /**
   * 🆕 Obtiene la clase CSS del tipo de movimiento
   */
  getMovementClass(movementType: number | string): string {
    const type = Number(movementType);
    switch (type) {
      case PortfolioMovementType.Withdrawal: return 'bg-red-100 border-red-400 text-red-800';
      case PortfolioMovementType.WithdrawalReversal: return 'bg-green-100 border-green-400 text-green-800';
      case PortfolioMovementType.Deferral: return 'bg-orange-100 border-orange-400 text-orange-800';
      default: return 'bg-gray-100 border-gray-400 text-gray-800';
    }
  }

  /**
   * 🆕 Obtiene el color del punto en la timeline
   */
  getTimelineDotClass(movementType: number | string): string {
    const type = Number(movementType);
    switch (type) {
      case PortfolioMovementType.Withdrawal: return 'bg-red-500';
      case PortfolioMovementType.WithdrawalReversal: return 'bg-green-500';
      case PortfolioMovementType.Deferral: return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  }

  getPaymentStatusClass(status: string) {
    return {
      'bg-white': true,
      'bg-green-50': status === 'PAID',
      'bg-yellow-50': status === 'PENDING',
      'bg-red-50': status === 'OVERDUE' || status === 'CANCELED'
    };
  }



  // Métodos nuevos para el resumen financiero
  getCompletedPayments(): number {
    return this.enrollment.paymentPlans.filter((p: any) =>
      p.paymentStatus === 'PAID').length;
  }

  getPendingPayments(): number {
    return this.enrollment.paymentPlans.filter((p: any) =>
      ['PENDING', 'OVERDUE'].includes(p.paymentStatus)).length;
  }

  getTotalAmount(): number {
    return this.enrollment.paymentPlans.reduce((acc: number, plan: any) =>
      acc + plan.amountToPay, 0);
  }

  getOverdueAmount(): number {
    return this.enrollment.paymentPlans
      .filter((p: any) => this.isPaymentOverdue(p))
      .reduce((acc: number, plan: any) =>
        acc + (plan.amountToPay - plan.amountPaid), 0);
  }

  // Métodos para el estado de los pagos
  isPaymentOverdue(plan: any): boolean {
    const dueDate = new Date(plan.dueDate);
    return dueDate < this.today && plan.paymentStatus !== 'PAID';
  }

  getDaysOverdue(plan: any): number {
    const dueDate = new Date(plan.dueDate);
    const diffTime = Math.abs(this.today.getTime() - dueDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  checkCurrentPayment(plan: any): boolean {
    const dueDate = new Date(plan.dueDate);
    return dueDate > this.today &&
      !this.enrollment.paymentPlans.some((p: any) =>
        new Date(p.dueDate) <= this.today && p.paymentStatus !== 'PAID'
      );
  }

  // Métodos existentes modificados
  getPaymentStatusLabel(status: string): string {
    switch (status) {
      case "PENDING":
        return 'Pendiente';
      case "PAID":
        return 'Pagado';
      case "OVERDUE":
        return 'Vencido';
      case "PARTIALLY_PAID":
        return 'Parcialmente Pagado';
      case "LINK_GENERATED":
        return 'Link generado';
      case "CANCELED":
        return 'Cancelado';
      default:
        return 'Desconocido';
    }
  }

  toggleCollapse(plan: any) {
    plan.isCollapsed = !plan.isCollapsed;
    if (typeof plan.isCollapsed === 'undefined') {
      plan.isCollapsed = false;
    }
  }

  formatCurrencyValue(amount: number, symbol?: string, code?: string): string {
    if (!amount && amount !== 0) return '';

    // Si no tenemos símbolo o código, usar por defecto COP
    const currencySymbol = symbol || '$';
    const currencyCode = code || 'COP';

    // Formatear el número con separador de miles
    const formattedAmount = new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    // Retornar con símbolo y código
    return `${currencySymbol}${formattedAmount} (${currencyCode})`;
  }

  getProjectInscriptionAmount(): string {
    if (!this.enrollment?.project) return '';

    const currencyCode = this.enrollment.currencyCode || 'COP';
    const symbol = this.enrollment.currencySymbol || '$';

    let amount = 0;
    if (currencyCode === 'COP') {
      amount = this.enrollment.project.inscriptionAmountCOP || 0;
    } else if (currencyCode === 'USD') {
      amount = this.enrollment.project.inscriptionAmountUSD || 0;
    } else {
      // Para otras monedas, usar USD como base
      amount = this.enrollment.project.inscriptionAmountUSD || 0;
    }

    return this.formatCurrencyValue(amount, symbol, currencyCode);
  }

  /**
   * Maneja la desactivación de un payment link
   * Recarga los datos de la inscripción para reflejar los cambios
   */
  onPaymentLinkDeactivated(): void {
    // Recargar los datos de la inscripción para reflejar el cambio de estado
    this.loadEnrollment();
  }

  /**
   * 🆕 Verifica si la inscripción es internacional
   */
  get isInternationalEnrollment(): boolean {
    return this.enrollment?.currencyCode &&
      this.enrollment.currencyCode !== 'COP' &&
      this.enrollment.currencyCode !== null;
  }

  /**
   * 🆕 Obtiene la tasa de cambio de la inscripción
   */
  /**
   * 🆕 Obtiene el código de moneda de la inscripción
   */
  get enrollmentCurrencyCode(): string | null {
    return this.enrollment?.currencyCode || this.enrollment?.project?.currency?.code || 'COP';
  }

  getEnrollmentExchangeRate(): number | null {
    return this.enrollment?.exchangeRateUsed || this.enrollment?.exchangeRate || null;
  }

  /**
   * 🆕 Obtiene la tasa de cambio correcta para un plan de pago
   * Prioridad: tasa del comprobante (si está pagado) > última cotización vigente (si está pendiente) > tasa de la inscripción
   */
  getPlanExchangeRate(plan: any): number | null {
    if (!this.isInternationalEnrollment) return null;

    // Si el plan está pagado y tiene comprobantes, usar la tasa del primer comprobante (histórica)
    if (plan.paymentStatus === 'PAID' && plan.incomeReceipts && plan.incomeReceipts.length > 0) {
      const receipt = plan.incomeReceipts[0]; // Usar el primer comprobante
      if (receipt.exchangeRate && receipt.exchangeRate !== null && receipt.exchangeRate !== 1) {
        return receipt.exchangeRate;
      }
    }

    // 🆕 Para planes pendientes, usar la última cotización vigente si está disponible
    if (plan.paymentStatus !== 'PAID' && this.currentExchangeRate && this.currentExchangeRate !== 1) {
      return this.currentExchangeRate;
    }

    // Fallback: usar la tasa de la inscripción
    return this.getEnrollmentExchangeRate();
  }

  /**
   * 🆕 Carga la última cotización vigente para la moneda de la inscripción
   */
  private loadCurrentExchangeRate(): void {
    if (!this.enrollmentCurrencyCode || this.enrollmentCurrencyCode === 'COP') {
      this.currentExchangeRate = 1;
      return;
    }

    this.exchangeRateService.getLatestRate(this.enrollmentCurrencyCode, 'COP').subscribe({
      next: (response: any) => {
        if (response.isSuccess && response.rate > 0) {
          this.currentExchangeRate = response.rate;
          console.log(`✅ Última cotización vigente cargada: ${this.currentExchangeRate} para ${this.enrollmentCurrencyCode}`);
        } else {
          // Fallback a tasa de la inscripción
          this.currentExchangeRate = this.getEnrollmentExchangeRate();
        }
      },
      error: (err) => {
        console.error('Error obteniendo última cotización vigente:', err);
        // Fallback a tasa de la inscripción
        this.currentExchangeRate = this.getEnrollmentExchangeRate();
      }
    });
  }

  /**
   * 🆕 Calcula el equivalente en COP de un monto para un plan de pago específico
   * Usa la tasa de cambio del comprobante si está pagado, o la de la inscripción si está pendiente
   */
  calculatePlanCOPEquivalent(plan: any, amount: number): string {
    if (!this.isInternationalEnrollment || !amount) return '';

    const rate = this.getPlanExchangeRate(plan);
    if (!rate) return 'N/A';

    const copAmount = amount * rate;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(copAmount);
  }

  /**
   * 🆕 Formatea la tasa de cambio de un plan de pago para mostrar
   */
  formatPlanExchangeRate(plan: any): string {
    const rate = this.getPlanExchangeRate(plan);
    if (!rate) return 'N/A';
    return new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(rate);
  }

  /**
   * Verifica si un recibo tiene tasa de cambio internacional
   */
  hasReceiptExchangeRate(receipt: any): boolean {
    return receipt?.exchangeRate && receipt.exchangeRate !== null && receipt.exchangeRate !== 1;
  }

  /**
   * Obtiene el equivalente en COP de un monto para un recibo con tasa de cambio
   */
  getReceiptCOPEquivalent(receipt: any, amount: number): string {
    if (!this.hasReceiptExchangeRate(receipt) || !receipt?.exchangeRate) {
      return 'N/A';
    }

    const copAmount = amount * receipt.exchangeRate;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(copAmount);
  }

  /**
   * 🆕 Calcula el total de ingresos generales
   */
  getTotalGeneralIncome(): number {
    if (!this.generalIncomeReceipts || this.generalIncomeReceipts.length === 0) {
      return 0;
    }
    return this.generalIncomeReceipts.reduce((sum, receipt) => sum + (receipt.netAmount || 0), 0);
  }

  // ============== MÉTODOS PARA CARTERA CASTIGADA ==============

  /**
   * Verifica si la inscripción tiene cuotas castigadas
   */
  hasPunishedInstallments(): boolean {
    if (!this.enrollment?.paymentPlans) return false;
    return this.enrollment.paymentPlans.some((p: any) => p.isPunished);
  }

  /**
   * Obtiene las cuotas castigadas
   */
  getPunishedInstallments(): any[] {
    if (!this.enrollment?.paymentPlans) return [];
    return this.enrollment.paymentPlans.filter((p: any) => p.isPunished);
  }

  /**
   * Calcula el total de cartera castigada
   */
  getTotalPunishedAmount(): number {
    return this.getPunishedInstallments()
      .reduce((sum: number, p: any) => sum + (p.amountToPay - (p.amountPaid || 0)), 0);
  }

  /**
   * Obtiene la etiqueta del estado de la inscripción
   */
  getEnrollmentStatusLabel(status: number | string): string {
    const statusMap: { [key: string]: string } = {
      '1': 'Activa',
      '2': 'Retirada',
      '3': 'Aplazada',
      '4': 'Completada',
      'Active': 'Activa',
      'Withdrawn': 'Retirada',
      'Deferred': 'Aplazada',
      'Completed': 'Completada',
      '8': 'Cesión de Cupo',
      'SlotTransfer': 'Cesión de Cupo'
    };
    return statusMap[String(status)] || 'Desconocido';
  }

  /**
   * Obtiene la clase CSS del estado de la inscripción
   */
  getEnrollmentStatusClass(status: number | string): string {
    const statusStr = String(status);
    if (statusStr === '1' || statusStr === 'Active') return 'bg-green-100 text-green-800';
    if (statusStr === '2' || statusStr === 'Withdrawn') return 'bg-red-100 text-red-800';
    if (statusStr === '3' || statusStr === 'Deferred') return 'bg-orange-100 text-orange-800';
    if (statusStr === '4' || statusStr === 'Completed') return 'bg-blue-100 text-blue-800';
    if (statusStr === '8' || statusStr === 'SlotTransfer') return 'bg-teal-100 text-teal-800';
    return 'bg-gray-100 text-gray-800';
  }

  /**
   * Verifica si la inscripción está retirada o aplazada
   */
  isEnrollmentWithdrawnOrDeferred(): boolean {
    if (!this.enrollment) return false;
    const status = String(this.enrollment.status);
    return status === '2' || status === '3' ||
      status === 'Withdrawn' || status === 'Deferred';
  }

  /**
   * Obtiene la etiqueta del tipo de movimiento
   */
  getMovementTypeLabel(movementType: number | string): string {
    const typeMap: { [key: string]: string } = {
      '0': 'Sin movimiento',
      '1': 'Retiro',
      '2': 'Reversión de Retiro',
      '3': 'Aplazamiento',
      'None': 'Sin movimiento',
      'Withdrawal': 'Retiro',
      'WithdrawalReversal': 'Reversión de Retiro',
      'Deferral': 'Aplazamiento'
    };
    return typeMap[String(movementType)] || 'Desconocido';
  }

  // ============== GESTIÓN DE CASTIGO MANUAL ==============

  isPunishMode: boolean = false;
  selectedPlanIds: number[] = [];
  punishReason: string = '';
  punishNotes: string = '';

  togglePunishMode(): void {
    this.isPunishMode = !this.isPunishMode;
    this.selectedPlanIds = [];
    this.punishReason = '';
    this.punishNotes = '';
  }

  togglePlanSelection(planId: number): void {
    const plan = this.enrollment.paymentPlans.find((p: any) => p.id === planId);
    if (plan && plan.paymentStatus === 'PAID') {
      return; // No permitir seleccionar cuotas pagadas
    }

    if (this.selectedPlanIds.includes(planId)) {
      this.selectedPlanIds = this.selectedPlanIds.filter(id => id !== planId);
    } else {
      this.selectedPlanIds.push(planId);
    }
  }

  isPlanSelected(planId: number): boolean {
    return this.selectedPlanIds.includes(planId);
  }

  selectAllPlans(): void {
    if (this.enrollment?.paymentPlans) {
      // Filtrar solo las cuotas no pagadas
      const availablePlans = this.enrollment.paymentPlans.filter((p: any) => p.paymentStatus !== 'PAID');

      if (this.selectedPlanIds.length === availablePlans.length) {
        this.selectedPlanIds = [];
      } else {
        this.selectedPlanIds = availablePlans.map((p: any) => p.id);
      }
    }
  }

  executePunishAction(action: 'punish' | 'unpunish'): void {
    if (this.selectedPlanIds.length === 0) {
      this.toastService.showToast('warning', 'Debes seleccionar al menos una cuota.');
      return;
    }

    if (!this.punishReason || this.punishReason.trim().length === 0) {
      this.toastService.showToast('warning', 'Debes ingresar una razón para esta acción.');
      return;
    }

    const request = {
      enrollmentId: this.enrollment.id,
      paymentPlanIds: this.selectedPlanIds,
      reason: this.punishReason,
      notes: this.punishNotes
    };

    this.loadingService.show();

    const observable = action === 'punish'
      ? this.portfolioMovementService.punishInstallments(request)
      : this.portfolioMovementService.unpunishInstallments(request);

    observable.subscribe({
      next: (response) => {
        if (response.isSuccess) {
          const message = response.data?.message || (action === 'punish' ? 'Cuotas castigadas exitosamente' : 'Cuotas descastigadas exitosamente');
          this.toastService.showToast('success', message);
          this.togglePunishMode();
          this.loadEnrollment(); // Recargar datos
        } else {
          this.toastService.showToast('error', response.errorMessage || 'Ocurrió un error al procesar la solicitud.');
        }
        this.loadingService.hide();
      },
      error: (err) => {
        console.error(`Error al ${action === 'punish' ? 'castigar' : 'descastigar'} cuotas:`, err);
        this.toastService.showToast('error', `Error al procesar la solicitud: ${err.message}`);
        this.loadingService.hide();
      }
    });
  }

}
